// src/features/dashboard/components/DashboardHeader.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileCopy as FileCopyIcon,
  NoteAdd as NoteAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Hooks
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { capitalizeFirst } from '../../../utils/formatters/capitalize';

import PageHeader from '../../../components/common/PageHeader';
import DashboardIcon from '@mui/icons-material/Dashboard';

const DashboardHeader = React.memo(function DashboardHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [departmentName, setDepartmentName] = useState('');
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);

  // Load department name from localStorage
  useEffect(() => {
    try {
      const lastLoginDepartment = localStorage.getItem('orbit_last_login_department');
      if (lastLoginDepartment) {
        setDepartmentName(lastLoginDepartment);
      }
    } catch (error) {
      console.error('Failed to load department from localStorage:', error);
    }
  }, []);

  const handleOpenCreateMenu = useCallback((event) => {
    setCreateMenuAnchor(event.currentTarget);
  }, []);

  const handleCloseCreateMenu = useCallback(() => {
    setCreateMenuAnchor(null);
  }, []);

  const handleCreateFromScratch = useCallback(() => {
    handleCloseCreateMenu();
    navigate('/tasks/create');
  }, [navigate, handleCloseCreateMenu]);

  const handleCreateFromTemplate = useCallback(() => {
    handleCloseCreateMenu();
    navigate('/tasks/catalog');
  }, [navigate, handleCloseCreateMenu]);

  // Check if user can create tasks
  const canCreateTask = permissions.hasScope('task:create');

  return (
    <PageHeader
      icon={DashboardIcon}
      title={t('common:dashboard.title')}
      subtitle={departmentName ? capitalizeFirst(departmentName) : undefined}
      actions={<>
        {canCreateTask && (
          <>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleOpenCreateMenu}
            >
              {t('common:dashboard.new_task')}
            </Button>

            <Menu
              anchorEl={createMenuAnchor}
              open={Boolean(createMenuAnchor)}
              onClose={handleCloseCreateMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleCreateFromTemplate}>
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t('tasks:list_page.create_from_template')}
                </ListItemText>
              </MenuItem>

              <MenuItem onClick={handleCreateFromScratch}>
                <ListItemIcon>
                  <NoteAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {t('tasks:list_page.create_from_scratch')}
                </ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </>}
    />
  );
});

export default DashboardHeader;